import { applyBenchmark } from '../benchmark';
import { getMetricSpec } from '../metrics';
export function sortByOrder(a, b) {
    return a.sortOrder - b.sortOrder;
}
/**
 * Print-only "each side" marking rule: find the reps metric among `items`,
 * falling back to the first item when there is no reps metric.
 */
export function findRepsIndexOrFirst(items, getMetricType) {
    const index = items.findIndex((item) => getMetricType(item) === 'reps');
    return index === -1 ? 0 : index;
}
export function formatExercisePayload(exercise, supersetLabel, isAlternate) {
    return {
        name: exercise.exerciseName,
        sortOrder: exercise.sortOrder,
        libraryExerciseId: exercise.libraryExerciseId,
        supersetLabel,
        instruction: exercise.instruction,
        numMetrics: exercise.metrics.length,
        metrics: exercise.metrics.map((metric) => ({
            sortOrder: metric.sortOrder,
            name: metric.metricName,
        })),
        sets: exercise.sets.map((set) => ({
            sortOrder: set.sortOrder,
            inputs: set.metrics.map((metric) => ({
                sortOrder: exercise.metrics.find((m) => m.metricId === metric.metricId)
                    ?.sortOrder ?? 0,
                value: metric.input,
            })),
        })),
        shorthandMetrics: [],
        isAlternate: !!isAlternate,
        alternateGroupId: isAlternate,
    };
}
export function getShorthandMetrics(payload, libraryExerciseId, applyBenchmarkData, exerciseMetrics, sets, metrics, t, blockType) {
    applyCondensedMetrics(payload, libraryExerciseId, applyBenchmarkData, exerciseMetrics, sets, metrics, t, true, undefined, blockType);
    return payload.shorthandMetrics ?? [];
}
export function applyExpandedMetrics(payload, libraryExerciseId, applyBenchmarkData, exerciseMetrics, sets, metrics) {
    const sortedMetrics = exerciseMetrics
        .map((m) => {
        return {
            sortOrder: m.sortOrder,
            name: m.metricName,
        };
    })
        .sort(sortByOrder);
    payload.numMetrics = sortedMetrics.length;
    payload.metrics = sortedMetrics;
    payload.sets = sets
        .map((set) => {
        return {
            sortOrder: set.sortOrder,
            inputs: exerciseMetrics
                .map((exerciseMetric) => {
                const setMetric = set.metrics.find((m) => m.metricId === exerciseMetric.metricId);
                const metric = metrics.find((m) => m.id === exerciseMetric.metricId);
                if (!metric) {
                    throw new Error(`Could not find metric with id ${exerciseMetric.metricId}. `);
                }
                return {
                    sortOrder: exerciseMetric.sortOrder,
                    value: getValueForMetric(setMetric, libraryExerciseId, exerciseMetric, applyBenchmarkData, metric),
                };
            })
                .sort(sortByOrder),
        };
    })
        .sort(sortByOrder);
}
export function applyCondensedMetrics(payload, libraryExerciseId, applyBenchmarkData, exerciseMetrics, sets, metrics, t, isMobile, 
// Print-only "each side" set-level marker: appends " (ea)" to this metric's value.
markedMetricId, blockType) {
    const isBowling = blockType === 'bowling';
    // Bowling deliveries are shoehorned into the sets/reps model: each
    // delivery type (Match/Training/Modified) is its own "set" carrying a
    // redundant Sets=1 metric, and rows for delivery types not scheduled on
    // this day are left with a blank Reps value. Drop both so the condensed
    // shorthand only shows deliveries that were actually planned, without the
    // meaningless "1 x 1" sets-counter prefix.
    const repsMetric = exerciseMetrics.find((m) => m.metricType === 'reps');
    const relevantSets = isBowling && repsMetric
        ? sets.filter((set) => set.metrics.some((m) => m.metricId === repsMetric.metricId && m.input.trim().length > 0))
        : sets;
    const relevantMetrics = isBowling
        ? exerciseMetrics.filter((m) => m.metricType !== 'sets')
        : exerciseMetrics;
    // Sort metrics by their original order
    const sortedMetrics = relevantMetrics.sort(sortByOrder);
    const groupedSets = [];
    // For each set, combine it with consecutive identical sets
    // Once a difference in a set is found, start over
    relevantSets.sort(sortByOrder).forEach((set) => {
        const setMetricValues = sortedMetrics.flatMap((metric) => {
            const setMetric = set.metrics.find((m) => m.metricId === metric.metricId);
            const exerciseMetric = exerciseMetrics.find((m) => m.metricId === metric.metricId);
            const metricConfig = metrics.find((m) => m.id === metric.metricId);
            if (!exerciseMetric || !metricConfig) {
                // This should never happen
                return [];
            }
            return {
                metricId: metric.metricId,
                name: metric.metricName,
                value: getValueForMetric(setMetric, libraryExerciseId, exerciseMetric, applyBenchmarkData, metricConfig),
            };
        });
        // We only need to make sure this matches the last set cataloged
        const lastGroup = groupedSets[groupedSets.length - 1];
        let matches = false;
        if (lastGroup) {
            // Compare all metric values to see if this set is identical to the previous
            matches =
                setMetricValues.length === lastGroup.metricValues.length &&
                    setMetricValues.every((metricValue) => {
                        const lastGroupMetric = lastGroup.metricValues.find((m) => m.metricId === metricValue.metricId);
                        return lastGroupMetric && lastGroupMetric.value === metricValue.value;
                    });
            if (matches) {
                lastGroup.count++;
            }
        }
        if (!lastGroup || !matches) {
            groupedSets.push({
                count: 1,
                metricValues: setMetricValues,
            });
        }
    });
    // Create shorthand metrics
    payload.shorthandMetrics = groupedSets.flatMap(({ count, metricValues }) => {
        if (metricValues.length === 0) {
            return [];
        }
        // First metric value (B in A x B @ C)
        const firstMetric = metricDisplay(metricValues[0], false, isMobile, metricValues[0]?.metricId === markedMetricId);
        // Remaining metrics (C in A x B @ C)
        let remainingMetricsText = '';
        if (metricValues.length > 1) {
            remainingMetricsText = metricValues
                .slice(1)
                .map((metric, index) => {
                // First metric after @ doesn't include name, others do, unless mobile
                if (isMobile && !metric.value) {
                    return;
                }
                return metricDisplay(metric, index !== 0, isMobile, metric.metricId === markedMetricId);
            })
                .filter(Boolean)
                .join(', ');
        }
        if (isMobile &&
            !isBowling &&
            !remainingMetricsText &&
            !metricValues[0]?.value) {
            return t('setCount', { count });
        }
        // Bowling deliveries aren't "sets" — omit the "N x " sets-counter prefix.
        if (isBowling) {
            return `${firstMetric}${remainingMetricsText ? ` @ ${remainingMetricsText}` : ''}`;
        }
        // If there are no remaining metrics, don't include the @ part
        return `${count} x ${firstMetric}${remainingMetricsText ? ` @ ${remainingMetricsText}` : ''}`;
    });
}
// If the metric value is present, use it; Otherwise we just use the metric name in italics
// TODO use metric alias/abbreviation once it's available
function metricDisplay(metric, showName = false, isMobile = false, marked = false) {
    if (!metric)
        return '';
    const suffix = marked ? ' (ea)' : '';
    if (!metric.value?.trim().length)
        return (isMobile ? metric.name : `<i>${metric.name}</i>`) + suffix;
    return `${showName ? metric.name : ''} ${metric.value}${suffix}`.trim();
}
function getValueForMetric(setMetric, libraryExerciseId, exerciseMetric, applyBenchmarkData, metric) {
    const spec = getMetricSpec(metric);
    const valueParseResult = spec.schema.safeParse(setMetric?.input ?? '');
    if (!valueParseResult.success) {
        return setMetric?.input ?? '';
    }
    // The dispatcher's return type is the broad `NumericMetric` union, but its
    // actual shape always matches whatever `spec` was built for (the same
    // invariant `applyBenchmarkDataToIntensity`/`applyKpiBenchmarkToMetricValue`
    // relied on before this call was unified) — restore that narrow type here.
    const value = applyBenchmark(valueParseResult.data, {
        metricType: exerciseMetric.metricType,
        metricId: exerciseMetric.metricId,
        benchmarkGridId: exerciseMetric.benchmarkGridId,
        libraryExerciseId,
    }, applyBenchmarkData);
    return spec.displayValue(value);
}
//# sourceMappingURL=metric-format.js.map